// PasswordPages.jsx — v1.0.3 Password Reset Hardening
//
// Two pages with production-grade UX:
//   ForgotPasswordPage — email input, always-generic success copy.
//   ResetPasswordPage  — strength meter, match validation, redirect-if-no-token.
//
// Generic error surface per playbook: map any {detail} array/object to a
// plain string before rendering (React will crash otherwise on 422 from FastAPI).
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Cake, Loader2, CheckCircle, Eye, EyeOff, AlertCircle } from "lucide-react";
import { API, axios } from "@/api";

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────
function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Mirror backend strength rule: ≥8 chars AND (≥1 number OR ≥1 special).
function checkStrength(pw) {
  if (!pw) return { score: 0, label: "", passed: false, reason: "" };
  const hasEnoughLen = pw.length >= 8;
  const hasNumber = /\d/.test(pw);
  const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(pw);
  if (!hasEnoughLen)
    return { score: 1, label: "Too short", passed: false, reason: "At least 8 characters." };
  if (!(hasNumber || hasSpecial))
    return { score: 2, label: "Weak", passed: false, reason: "Add a number or special character." };
  if (pw.length >= 12 && hasNumber && hasSpecial)
    return { score: 4, label: "Strong", passed: true, reason: "" };
  return { score: 3, label: "Good", passed: true, reason: "" };
}

const STRENGTH_COLORS = {
  0: "bg-slate-100",
  1: "bg-red-400",
  2: "bg-orange-400",
  3: "bg-amber-400",
  4: "bg-emerald-500",
};
const STRENGTH_LABEL_COLORS = {
  0: "text-slate-400",
  1: "text-red-500",
  2: "text-orange-500",
  3: "text-amber-600",
  4: "text-emerald-600",
};

// ─────────────────────────────────────────────────────────────────
// ForgotPasswordPage
// ─────────────────────────────────────────────────────────────────
export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async () => {
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSent(true);
    } catch (e) {
      // 429 rate-limit gets real copy; everything else stays generic.
      const status = e?.response?.status;
      if (status === 429) {
        setError(formatApiErrorDetail(e.response?.data?.detail));
      } else {
        setError(formatApiErrorDetail(e.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-orange-50 flex flex-col items-center justify-center p-6">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-sky-100 mb-4">
            <Cake className="w-8 h-8 text-sky-500" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Reset Password</h1>
          <p className="text-slate-500 mt-2 text-sm">We'll send you a link to reset it</p>
        </div>
        <div className="bg-white rounded-3xl shadow-xl p-6 space-y-5">
          {sent ? (
            <div className="text-center py-4" data-testid="forgot-success">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-100 mb-4">
                <CheckCircle className="w-7 h-7 text-green-500" />
              </div>
              <p className="text-slate-800 font-medium">Check your inbox</p>
              <p className="text-slate-500 text-sm mt-2">
                If an account exists for that email, a reset link has been sent. It expires in 30 minutes.
              </p>
              <button
                onClick={() => navigate("/login")}
                className="mt-6 text-sky-500 font-medium text-sm"
                data-testid="back-to-login"
              >
                Back to Sign In
              </button>
            </div>
          ) : (
            <>
              <div>
                <label className="text-sm font-medium text-slate-600 block mb-2">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition"
                  placeholder="you@example.com"
                  data-testid="forgot-email"
                  autoComplete="email"
                  autoCapitalize="off"
                  spellCheck="false"
                />
              </div>
              {error && (
                <div className="flex items-start gap-2 text-red-600 text-sm" data-testid="forgot-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              <button
                onClick={handleSubmit}
                disabled={loading || !email.trim()}
                className="w-full py-4 rounded-full bg-gradient-to-r from-sky-400 to-sky-500 text-white font-semibold shadow-lg shadow-sky-500/30 transition-all disabled:opacity-50"
                data-testid="forgot-submit"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Send Reset Link"}
              </button>
              <p className="text-center text-sm text-slate-500">
                Remember your password?{" "}
                <button
                  onClick={() => navigate("/login")}
                  className="text-sky-500 font-medium"
                  data-testid="forgot-back-login"
                >
                  Sign In
                </button>
              </p>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// ResetPasswordPage
// ─────────────────────────────────────────────────────────────────
export function ResetPasswordPage() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const token = useMemo(
    () => new URLSearchParams(window.location.search).get("token"),
    [],
  );

  const strength = useMemo(() => checkStrength(password), [password]);
  const matchError = confirmPassword && confirmPassword !== password;

  // Redirect to /forgot-password immediately if the URL has no token
  useEffect(() => {
    if (!token) {
      navigate("/forgot-password", { replace: true });
    }
  }, [token, navigate]);

  const canSubmit = token && password && confirmPassword && !matchError && strength.passed && !loading;

  const handleSubmit = async () => {
    setError("");
    if (!strength.passed) {
      setError(strength.reason || "Password does not meet requirements.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (!token) return null; // redirect effect above will fire

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-orange-50 flex flex-col items-center justify-center p-6">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-sky-100 mb-4">
            <Cake className="w-8 h-8 text-sky-500" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Set New Password</h1>
          <p className="text-slate-500 mt-2 text-sm">Pick something strong you'll remember</p>
        </div>
        <div className="bg-white rounded-3xl shadow-xl p-6 space-y-5">
          {success ? (
            <div className="text-center py-4" data-testid="reset-success">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-100 mb-4">
                <CheckCircle className="w-7 h-7 text-green-500" />
              </div>
              <p className="text-slate-800 font-medium">Password reset</p>
              <p className="text-slate-500 text-sm mt-2">
                You can now sign in with your new password.
              </p>
              <button
                onClick={() => navigate("/login")}
                className="mt-6 px-8 py-3 rounded-full bg-sky-500 text-white font-semibold shadow-lg"
                data-testid="reset-go-login"
              >
                Sign In
              </button>
            </div>
          ) : (
            <>
              <div>
                <label className="text-sm font-medium text-slate-600 block mb-2">New Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 rounded-xl border border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition"
                    placeholder="8+ characters with a number or symbol"
                    data-testid="reset-password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {password && (
                  <div className="mt-2" data-testid="reset-strength">
                    <div className="flex gap-1">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className={`h-1 flex-1 rounded-full transition-colors ${
                            i <= strength.score ? STRENGTH_COLORS[strength.score] : "bg-slate-100"
                          }`}
                        />
                      ))}
                    </div>
                    <p className={`text-xs mt-1 ${STRENGTH_LABEL_COLORS[strength.score]}`}>
                      {strength.label}{strength.reason ? ` — ${strength.reason}` : ""}
                    </p>
                  </div>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600 block mb-2">Confirm Password</label>
                <input
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border outline-none transition ${
                    matchError
                      ? "border-red-300 focus:ring-2 focus:ring-red-200"
                      : "border-slate-200 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                  }`}
                  placeholder="Re-enter password"
                  data-testid="reset-confirm"
                  autoComplete="new-password"
                />
                {matchError && (
                  <p className="text-red-500 text-xs mt-1" data-testid="reset-match-error">Passwords don't match.</p>
                )}
              </div>
              {error && (
                <div className="flex items-start gap-2 text-red-600 text-sm" data-testid="reset-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="w-full py-4 rounded-full bg-gradient-to-r from-sky-400 to-sky-500 text-white font-semibold shadow-lg shadow-sky-500/30 transition-all disabled:opacity-50"
                data-testid="reset-submit"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Reset Password"}
              </button>
              <p className="text-center text-sm text-slate-500">
                Need a new link?{" "}
                <button
                  onClick={() => navigate("/forgot-password")}
                  className="text-sky-500 font-medium"
                  data-testid="reset-new-link"
                >
                  Request one
                </button>
              </p>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
